import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, KeyRound, AlertTriangle, Key } from 'lucide-react'
import { useAuth } from '../lib/auth'
import { api } from '../lib/api'
import { getPasskey, isWebAuthnSupported } from '../lib/webauthn'

const SSO_ERROR_MESSAGES: Record<string, string> = {
  token_exchange_failed: 'Microsoft rejected the authorisation code.',
  id_token_invalid: 'ID token failed verification.',
  user_not_authorised: 'Your account is not authorised. Ask an admin to add you.',
  no_email: 'Microsoft did not return an email for your account.',
  no_id_token: 'Microsoft did not return an ID token.',
  network_error: 'Could not reach Microsoft.',
  user_lookup_failed: 'Could not create or load your user record.',
  access_denied: 'Sign-in was cancelled.',
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, verifyMFA, loginWithToken, mfaRequired, loading, error, isAuthenticated } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [ssoEnabled, setSsoEnabled] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [passkeyError, setPasskeyError] = useState<string | null>(null)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  // 1) Check if Microsoft SSO is configured on the server
  useEffect(() => {
    api.get('/api/auth/microsoft/config')
      .then(r => setSsoEnabled(!!r.data?.enabled))
      .catch(() => setSsoEnabled(false))
  }, [])

  // 2) If we got back from Microsoft SSO with a token, finish the login
  useEffect(() => {
    const token = searchParams.get('sso_token')
    const ssoEmail = searchParams.get('sso_email')
    const err = searchParams.get('sso_error')
    if (token && ssoEmail) {
      loginWithToken(token, ssoEmail)
      // clean the URL
      setSearchParams({}, { replace: true })
    } else if (err) {
      setSsoError(SSO_ERROR_MESSAGES[err] || `SSO error: ${err}`)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, loginWithToken])

  // Redirect to dashboard once authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/app', { replace: true })
  }, [isAuthenticated, navigate])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    await login(email, password)
  }

  const submitMFA = async (code: string) => {
    if (code.length === 6) {
      await verifyMFA(email, password, code)
    }
  }

  const handleMFA = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitMFA(mfaCode)
  }

  // ── Passkey / WebAuthn login (Track 5.6) ────────────────────────────────────
  const usePasskey = async () => {
    setPasskeyError(null)
    if (!isWebAuthnSupported()) {
      setPasskeyError('Passkeys require a secure context (HTTPS or localhost). Open the dashboard via HTTPS.')
      return
    }
    if (!email || !password) {
      setPasskeyError('Enter your email and password first — passkey is an additional factor.')
      return
    }
    setPasskeyLoading(true)
    try {
      const beginRes = await api.post('/api/auth/webauthn/authenticate/begin', { user_email: email })
      const assertion = await getPasskey(beginRes.data)
      const finishRes = await api.post('/api/auth/webauthn/authenticate/finish', {
        user_email: email,
        password,
        credential: assertion,
      })
      loginWithToken(finishRes.data.access_token, finishRes.data.email)
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Passkey sign-in failed'
      setPasskeyError(String(msg))
    } finally {
      setPasskeyLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-blue flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">CT</span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">TechDashboard</h1>
          <p className="text-sm text-text-secondary mt-1">Real-time technology intelligence</p>
        </div>

        <div className="card p-6">
          {!mfaRequired ? (
            /* Step 1: Email + Password */
            <form onSubmit={handleLogin}>
              <div className="flex items-center gap-2 mb-6">
                <Lock size={18} className="text-blue" />
                <h2 className="text-sm font-semibold text-text-primary">Sign In</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="text-xs text-text-secondary block mb-1.5">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="input"
                    placeholder="you@example.com"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="password" className="text-xs text-text-secondary block mb-1.5">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="input"
                    placeholder="Enter password"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 mt-4 text-xs text-rose-light">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full mt-6">
                {loading ? 'Verifying...' : 'Continue'}
              </button>

              {ssoEnabled && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-[10px] uppercase tracking-wider text-text-muted">or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <a
                    href="/api/auth/microsoft/login"
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    {/* Microsoft 4-square logo */}
                    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    <span className="text-xs">Sign in with Microsoft</span>
                  </a>
                </>
              )}

              {ssoError && (
                <div className="flex items-center gap-2 mt-4 text-xs text-rose-light">
                  <AlertTriangle size={14} /> {ssoError}
                </div>
              )}
            </form>
          ) : (
            /* Step 2: MFA Code */
            <form onSubmit={handleMFA}>
              <div className="flex items-center gap-2 mb-6">
                <KeyRound size={18} className="text-amber" />
                <h2 className="text-sm font-semibold text-text-primary">Enter MFA Code</h2>
              </div>

              <p className="text-xs text-text-secondary mb-4">
                Enter the 6-digit code from your authenticator app. Codes are valid for 90 seconds.
              </p>

              <div>
                <label htmlFor="mfa" className="text-xs text-text-secondary block mb-1.5">MFA Code</label>
                <input
                  id="mfa"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={mfaCode}
                  onChange={e => {
                    const clean = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setMfaCode(clean)
                    if (clean.length === 6) submitMFA(clean)
                  }}
                  onPaste={e => {
                    e.preventDefault()
                    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
                    setMfaCode(pasted)
                    if (pasted.length === 6) submitMFA(pasted)
                  }}
                  className="input text-center text-2xl tracking-[0.3em] font-mono"
                  placeholder="000000"
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 mt-4 text-xs text-rose-light">
                  <AlertTriangle size={14} /> {error}
                </div>
              )}

              <button type="submit" disabled={loading || mfaCode.length !== 6} className="btn-primary w-full mt-6">
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>

              {/* Passkey alternative — phishing-resistant (Track 5.6) */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-text-muted">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <button
                type="button"
                onClick={usePasskey}
                disabled={passkeyLoading}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <Key size={14} />
                <span className="text-xs">{passkeyLoading ? 'Checking passkey…' : 'Use passkey'}</span>
              </button>
              {passkeyError && (
                <div className="flex items-start gap-2 mt-3 text-xs text-rose-light">
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> <span>{passkeyError}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => useAuth.setState({ mfaRequired: false, error: null })}
                className="btn-ghost w-full mt-2 text-xs"
              >
                Back to login
              </button>
            </form>
          )}
        </div>

        <p className="text-[10px] text-text-muted text-center mt-6">
          Protected by JWT + TOTP MFA (90s window)
        </p>
      </div>
    </div>
  )
}
